import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Spinner from './Spinner';
import HeaderATS from './HeaderAts';
import FooterAts from './FooterAts';
import ImageGallery from './ImageGallery';
import { Calculator } from 'heroicons-react';
import {  Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import PaymentCalculator from './PaymentCalculator';


const CarDetails = () => {
  const { id } = useParams();
  const [carDetails, setCarDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [activeTab, setActiveTab] = useState('features');
  const wpURL = process.env.REACT_APP_WORDPRESS_URL;
  
  // Calculator modal
  let [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch car details
        const response = await fetch(`${wpURL}/wp-json/wp/v2/auto-listing/${id}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch car details (${response.status})`);
        }
        
        const carData = await response.json();

        // Set car details
        setCarDetails(carData);
        
        // Set active image to the first gallery image or featured image
        if (carData.meta_box._al_listing_image_gallery.length > 0 && carData.meta_box._al_listing_image_gallery[0].sizes.large) {
            setActiveImage(carData.meta_box._al_listing_image_gallery[0].sizes.large.url);
        } else if (carData.meta_box._al_listing_image_gallery.length > 0 && carData.meta_box._al_listing_image_gallery[0].sizes.thumbnail) {
            setActiveImage(carData.meta_box._al_listing_image_gallery[0].sizes.thumbnail.url);
        }
        
        setError(null);
        } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error fetching car details:', err);
        } finally {
        setLoading(false);
        }
        };

        if (id) {
        fetchCarDetails();
        }
    }, [id, wpURL]);

    // Format price as currency
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
        }).format(price);
    };

    // Format mileage with commas
    const formatMileage = (mileage) => {
        return new Intl.NumberFormat('en-US').format(mileage);
    };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error || !carDetails) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-medium text-red-800">Error Loading Vehicle</h2>
          <p className="mt-2 text-red-700">{error || 'Unable to load vehicle details'}</p>
          <button 
            onClick={() => window.location.href = '/inventory'}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
          >
            Return to Inventory
          </button>
        </div>
      </div>
    );
  }

  // Get vehicle name for breadcrumbs
  const vehicleName = `${carDetails.meta_box._al_listing_model_year} ${carDetails.meta_box._al_listing_make_display} ${carDetails.meta_box._al_listing_model_name}`;

  return (
    <div className='page'>
      <HeaderATS /> 
      
      {/* Breadcrumbs Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ol className="flex items-center h-12 space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-500 hover:text-gray-700">
                Home
              </Link>
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <Link to="/inventory" className="text-gray-500 hover:text-gray-700">
                Inventory
              </Link>
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-900 font-medium truncate" aria-current="page">
                {vehicleName}
              </span>
            </li>
          </ol>
        </div>
      </nav>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 lg:items-start">
          
          {/* Image gallery */}
          {carDetails.meta_box._al_listing_image_gallery.length > 0
          ?
          <ImageGallery
            carDetails={carDetails}
            activeImage={activeImage}
            setActiveImage={setActiveImage}
          />
          :
          <div className="lg:col-span-8">
            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center text-2xl text-gray-500">
              No images available
            </div>
          </div>
          }

          {/* Car details */}
          <div className="mt-10 lg:mt-0 lg:col-span-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {carDetails.meta_box._al_listing_model_year} {carDetails.meta_box._al_listing_make_display} {carDetails.meta_box._al_listing_model_name}
              <br/>
              <span className="text-lg text-slate-600 font-normal">
              {carDetails.meta_box._al_listing_model_vehicle} {carDetails.meta_box._al_listing_model_drive}
              </span>
            </h1>
            
            <div className="mt-4">
              <h2 className="sr-only">Price</h2>
              {carDetails.meta_box._al_listing_price && carDetails.meta_box._al_listing_listing_state !== 'Sold'
              ?
              <p className="text-3xl font-bold text-red-600">{formatPrice(carDetails.meta_box._al_listing_price)}</p>
              :
              <p className="text-3xl font-bold text-gray-600">Sold</p>
              }
            </div>
            
            {/* Specs */}
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900">Highlights</h3>
              
              <div className="mt-4 grid grid-cols-2 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Mileage</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{formatMileage(carDetails.meta_box._al_listing_odometer)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">VIN</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.acf.vin ? carDetails.acf.vin : 'Not available'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Engine</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.acf.engine ? carDetails.acf.engine : '--'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Transmission</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.meta_box._al_listing_model_transmission_type ? carDetails.meta_box._al_listing_model_transmission_type : '--'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Exterior Color</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.acf.exterior_color ? carDetails.acf.exterior_color : '--'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Interior Color</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.acf.interior_color ? carDetails.acf.interior_color : '--'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fuel Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.meta_box._al_listing_model_engine_fuel ? carDetails.meta_box._al_listing_model_engine_fuel : '--'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fuel Economy</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">{carDetails.acf.fuel_economy_city ? `${carDetails.acf.fuel_economy_city} city / ${carDetails.acf.fuel_economy_hwy} hwy` : '--'}</dd>
                </div>
              </div>

              <hr className="mt-6 border-t border-gray-200" />

              {/* Title Brand and Stock number */}
              <div className='flex items-center justify-between mt-4'>
              <p className="mt-4 text-sm text-slate-600">
                {carDetails.meta_box._al_listing_registration ? carDetails.meta_box._al_listing_registration + ' Title' : 'No title brand'}
              </p>
              <p className="mt-4 text-sm text-slate-600">
                Stock #{carDetails.acf.stock_no ? carDetails.acf.stock_no : 'No stock number'}
              </p>
              </div>

            </div>


            {/* Call to action */}
            {carDetails.meta_box._al_listing_listing_state !== 'Sold' && (
            <div className="mt-8 space-y-4">
              <Link to="/contact"
                className="w-full bg-red-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-red-700"
              >
                Contact About This Vehicle
              </Link>
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="w-full bg-red-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-red-700"
              >
                <Calculator className="h-5 w-5 mr-2" /> Calculate Payment
              </button>
              <button
                type="button"
                disabled
                className="w-full bg-gray-200 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-gray-800"
              >
                Apply for Financing
              </button>
            </div>
            )}

          </div>
        </div>
          
        {/* Description and Dealer Notes - Full width below the main content */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          {/* Tab navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Vehicle information tabs">
              <button
                onClick={() => setActiveTab('features')}
                className={`${
                  activeTab === 'features'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Features
              </button>
              {carDetails.content.rendered && (
              <button
                onClick={() => setActiveTab('description')}
                className={`${
                  activeTab === 'description'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Description
              </button>
              )}
              {carDetails.acf.dealer_notes && (
              <button
                onClick={() => setActiveTab('dealerNotes')}
                className={`${
                  activeTab === 'dealerNotes'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Dealer Notes
              </button>
              )}
            </nav>
          </div>
          
          {/* Tab content */}
          <div className="py-6">
            {activeTab === 'features' && (
              <div className="prestyled prose prose-sm max-w-none text-gray-500">
                <div dangerouslySetInnerHTML={{ __html: carDetails.acf.features }} />
              </div>
            )}

            {activeTab === 'description' && (
              <div className="prestyled prose prose-sm max-w-none text-gray-500">
                <div dangerouslySetInnerHTML={{ __html: carDetails.content.rendered }} />
              </div>
            )}
            
            {activeTab === 'dealerNotes' && (
              <div className="prestyled prose prose-sm max-w-none text-gray-500">
                <div dangerouslySetInnerHTML={{ __html: carDetails.acf.dealer_notes }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <>
      <Dialog open={isOpen} onClose={setIsOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-800/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white/0 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in p-4 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <PaymentCalculator selectedVehiclePrice={carDetails.meta_box._al_listing_price} onClose={setIsOpen} />
          </DialogPanel>
        </div>
        </div>
      </Dialog>
    </>

      <FooterAts />
    </div>
  );
};

export default CarDetails;